import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
import {Hub, MarketplaceApp} from 'yougile-model'
import {Api} from '@api/api'
import {Runner} from '@api/runner'
import {MarketplaceClient} from '@client/marketplace-client'
import {Sync} from '@client/sync'
import {Tracking, TrackingEvent} from '@common/tracking'
import {InputApi} from '@components/common/base/input/types'
import {showConfirm} from '@components/common/base/notifications'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {ScriptsStore} from './scripts-store'

interface State {
  edited: Hub | MarketplaceApp
  addNew: boolean
  openedTab: Hub | MarketplaceApp
  tabs: Array<Hub | MarketplaceApp>
  codes: {[scriptId: string]: string}
  changed: {[scriptId: string]: boolean}
  subscribedToChanges?: boolean
  listScripts: Hub[]
  companyChangeListener?: number
}

export class ScriptsCodeStore {
  state: State

  constructor(public store: ScriptsStore) {
    this.state = {
      edited: undefined,
      addNew: false,
      openedTab: undefined,
      tabs: [],
      codes: {},
      changed: {},
      listScripts: []
    }

    makeAutoObservable(
      this,
      {
        state: observable.shallow,
        store: false
      },
      {autoBind: true}
    )

    this.state.companyChangeListener = globals.onCompanyChange(() => {
      this.state.tabs = []
      this.state.openedTab = undefined
      this.state.codes = {}
      this.updateScriptsList()
      this.syncCodes()
      this.syncTabs()
    })

    this.updateScriptsList()
  }

  dispose(): void {
    globals.removeCompanyListener(this.state.companyChangeListener)
  }

  submit(edit: InputApi): void {
    if (!edit.text || (!this.state.edited && !this.state.addNew)) {
      this.cancel()
      return
    }
    if (edit.text.indexOf('.js') !== edit.text.length - 3) {
      if (!this.state.addNew) {
        MessageBox.warn(Lang.msg.onlyJsAllowed)
        this.cancel()
        return
      } else {
        edit.text += '.js'
      }
    }
    const box = Sync.getCurrentSandbox()
    if (this.state.addNew) {
      const newScript = new Hub(box.companyId, edit.text)
      Api.addScript(newScript)
      this.updateScriptsList()
      void Tracking.send(TrackingEvent.scriptsCreate)
      Api.enableScript(newScript)
    } else {
      if (this.state.edited instanceof Hub) {
        box.editTitle(this.state.edited, edit.text)
      }
    }
    this.cancel()
  }

  updateScriptsList(): void {
    this.state.listScripts = Api.listScripts(Sync.getCurrentSandbox())
  }

  cancel(): void {
    this.state.edited = undefined
    this.state.addNew = false
  }

  copyApp(app: MarketplaceApp): void {
    logger.log('copy', app)
    const box = Sync.getCurrentSandbox()
    const newScript = new Hub(box.companyId, app.name)
    newScript.description = app.code
    Api.addScript(newScript)
    this.updateScriptsList()
    Api.enableScript(newScript)
    if (this.state.openedTab && this.state.openedTab.id === app.id) {
      const ix = this.state.tabs.findIndex(
        (i: Hub | MarketplaceApp) => i.id === this.state.openedTab.id
      )
      this.state.tabs.splice(ix, 1, newScript)
      this.syncTabs()
      this.state.openedTab = newScript
    }
  }

  getHint(script: Hub | MarketplaceApp): string {
    const code = this.getCode(script)
    const commentLines: string[] = []
    let inside = false
    for (const lineNoTrim of code.split('\n')) {
      const line = lineNoTrim.trim()
      if (!inside) {
        if (line && !line.startsWith('/*')) {
          break
        }
        if (line.includes('/*')) {
          inside = true
          commentLines.push(
            line.replace(/\*\/.*$/g, '').replace(/^ *\/?\*+ */g, '')
          )
          if (line.includes('*/')) {
            break
          }
        }
      } else {
        commentLines.push(
          line.replace(/\*\/.*$/g, '').replace(/^ *\/?\*+ */g, '')
        )
        if (line.includes('*/')) {
          break
        }
      }
    }
    return commentLines.join(' ')
  }

  getCode(script: Hub | MarketplaceApp): string {
    return script instanceof Hub ? script.description : script.code
  }

  getName(script: Hub | MarketplaceApp): string {
    return script instanceof Hub ? script.title : script.name
  }

  isEnabled(script: Hub | MarketplaceApp): boolean {
    return script instanceof Hub
      ? Api.isEnabledScript(script)
      : MarketplaceClient.isEnabled(this.store.globalState.companyId, script.id)
  }

  toggleEnabled(script: Hub | MarketplaceApp): void {
    if (script instanceof Hub) {
      if (Api.isEnabledScript(script)) {
        Runner.unloadJs(script.title, this.store.globalState.companyId)
      } else {
        Runner.unloadJs(script.title, this.store.globalState.companyId)
        const code = this.state.codes[script.id] || script.description
        Runner.runJs(
          code,
          script.title,
          this.store.globalState.companyId,
          undefined
        )
      }
      Api.toggleEnableScript(script)
    } else {
      if (
        MarketplaceClient.isEnabled(this.store.globalState.companyId, script.id)
      ) {
        MarketplaceClient.disable(this.store.globalState.companyId, script.id)
        Runner.unloadJs(script.name, this.store.globalState.companyId)
      } else {
        MarketplaceClient.enable(this.store.globalState.companyId, script.id)
        Runner.unloadJs(script.name, this.store.globalState.companyId)
        const code = this.state.codes[script.id] || script.code
        Runner.runJs(
          code,
          script.name,
          this.store.globalState.companyId,
          undefined
        )
      }
    }
  }

  async deleteScript(hub: Hub): Promise<void> {
    if (await showConfirm(Lang.msg.confirmDeleteScript(hub.title))) {
      Sync.get(hub).remove(hub)
      this.updateScriptsList()
    }
  }

  enableEdit(script: Hub): void {
    this.state.edited = script
    this.state.addNew = false
  }

  listApps(): MarketplaceApp[] {
    return MarketplaceClient.apps
  }

  uploadScript(): void {
    Api.loadScript(Sync.getCurrentSandbox(), this.updateScriptsList)
  }

  async reloadScripts(): Promise<void> {
    if (await showConfirm(Lang.msg.reloadScriptsConfirm)) {
      Api.reloadScriptsForAll()
    }
  }

  openTab(script: Hub | MarketplaceApp): void {
    if (!this.state.tabs.find((tab: Hub) => tab.id === script.id)) {
      this.state.tabs.push(script)
      this.state.codes[script.id] = this.getCode(script)
      this.state.changed = {...this.state.changed, [script.id]: false}

      this.syncTabs()
      this.syncCodes()
    }
    this.state.openedTab = script
    setTimeout(() => {
      if (!this.store.globalState.monaco) {
        return
      }
      this.store.globalState.monacoTabId = script.id
      this.store.globalState.monaco.setValue(this.state.codes[script.id])
      this.store.globalState.monaco.open()

      this.subscribeToChanges()
    })
  }

  subscribeToChanges(): void {
    if (this.state.subscribedToChanges) {
      return
    }
    this.state.subscribedToChanges = true
    this.store.globalState.monaco.instance.getModel().onDidChangeContent(() => {
      this.updateChanged(this.state.openedTab)
    })
  }

  updateChanged(script: Hub | MarketplaceApp): void {
    const value = this.store.globalState.monaco.getValue()
    this.state.changed = {
      ...this.state.changed,
      [script.id]: value !== this.getCode(this.state.openedTab)
    }
  }

  async closeTab(script: Hub | MarketplaceApp): Promise<void> {
    const ix = this.state.tabs.findIndex((tab: Hub) => tab.id === script.id)
    if (
      this.state.codes[script.id] &&
      this.getCode(script) !== this.state.codes[script.id]
    ) {
      if (
        script instanceof Hub &&
        !(await showConfirm(Lang.msg.confirmCloseUnsaved))
      ) {
        return
      }
    }
    if (ix > -1) {
      this.state.tabs.splice(ix, 1)
      this.syncTabs()
    }
    if (this.state.openedTab && this.state.openedTab.id === script.id) {
      this.state.openedTab = undefined
    }
    this.store.globalState.monacoTabId = undefined
    this.state.codes[script.id] = undefined
    this.syncCodes()
  }

  saveScript(): void {
    if (this.state.openedTab && this.state.openedTab instanceof Hub) {
      const hub = this.state.openedTab
      if (this.store.globalState.monacoTabId === hub.id) {
        this.state.codes[hub.id] = this.store.globalState.monaco.getValue()
        this.syncCodes()
      }
      hub.description = this.state.codes[hub.id] || ''
      Api.saveScript(hub)
      this.updateChanged(hub)
    }
  }

  runScript(): void {
    if (this.state.openedTab && this.state.openedTab instanceof Hub) {
      const hub = this.state.openedTab
      if (this.store.globalState.monacoTabId === hub.id) {
        this.state.codes[hub.id] = this.store.globalState.monaco.getValue()
        this.syncCodes()
      }
      Runner.unloadJs(hub.title, hub.companyId)
      const code = this.state.codes[hub.id] || ''
      Runner.runJs(code, hub.title, hub.companyId, undefined)
    }
  }

  syncCodes(): void {
    this.state.codes = {...this.state.codes}
  }

  syncTabs(): void {
    this.state.tabs = [...this.state.tabs]
  }

  setNew(value: boolean): void {
    this.state.addNew = value
    this.state.edited = undefined
  }
}

export const [ScriptsCodeContext, useScriptsCodeStore] =
  createUseStore(ScriptsCodeStore)
